<template>
  <v-dialog v-model="model" max-width="480" scrollable content-class="customer-dialog-overlay">
    <v-card rounded="lg" elevation="12" class="customer-dialog-card">
      <v-card-title class="d-flex align-center justify-space-between px-5 pt-5 pb-2">
        <span class="text-h6 font-weight-bold">{{ dialogTitle }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          density="comfortable"
          aria-label="Close"
          @click="model = false"
        />
      </v-card-title>
      <v-divider class="mx-0" />
      <v-card-text class="px-5 py-4">
        <Loading v-if="state.isLoading" message="Saving prompt..." />
        <template v-else>
          <v-text-field
            v-if="!isDefaultPrompt"
            v-model="form.title"
            label="Title"
            variant="outlined"
            density="comfortable"
            class="mb-4"
            hide-details="auto"
          />
          <v-textarea
            v-model="form.content"
            label="Content"
            :readonly="isDefaultPrompt"
            variant="outlined"
            rows="6"
            auto-grow
            hide-details="auto"
          />
        </template>
      </v-card-text>
      <v-card-actions class="px-5 pb-4 pt-0">
        <v-spacer />
        <template v-if="isDefaultPrompt">
          <v-btn variant="text" class="text-transform-none" @click="model = false">Close</v-btn>
        </template>
        <template v-else>
          <v-btn
            variant="text"
            class="text-transform-none"
            :disabled="state.isLoading"
            @click="model = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="text-transform-none"
            :disabled="state.isLoading"
            :loading="state.isLoading"
            @click="savePrompt"
          >
            Save
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useProfileStore } from "@/stores/profile";
import { IApp } from "@/types";
import Loading from "./Loading.vue";

const props = defineProps<{
  modelValue: boolean;
  prompt?: IApp.IPrompt | null;
}>();

const state = ref({
  isLoading: false
});

const emit = defineEmits(["update:modelValue"]);

const model = ref(props.modelValue);
watch(
  () => props.modelValue,
  (val) => (model.value = val)
);
watch(model, (val) => emit("update:modelValue", val));

const store = useProfileStore();
const isEdit = ref(false);
const form = ref({ title: "", content: "", id: "" });

const isDefaultPrompt = computed(() => props.prompt?.id === "default");

const dialogTitle = computed(() => {
  if (isDefaultPrompt.value) {
    return "System default prompt";
  }
  return isEdit.value ? "Edit prompt" : "New prompt";
});

watch(
  () => props.prompt,
  (val) => {
    if (val) {
      form.value = { ...val };
      isEdit.value = true;
    } else {
      form.value = { title: "", content: "", id: "" };
      isEdit.value = false;
    }
  }
);

async function savePrompt() {
  state.value.isLoading = true;
  if (isEdit.value) {
    await store.updatePrompt({
      id: form.value.id,
      title: form.value.title,
      content: form.value.content
    });
  } else {
    await store.createPrompt({ title: form.value.title, content: form.value.content });
  }
  model.value = false;
  state.value.isLoading = false;
}
</script>
